import {
  DurationRefinement,
  ACTIVITIES_APPLIED_FILTER,
  ExperiencesAvailabilityAutocompleteResponse,
  ExperiencesAvailabilityAutocompleteResult,
  ExperiencesAvailabilityRequest,
  ExperiencesAvailabilityRequestEnum,
  ExperiencesAvailabilityResponse,
  ExperiencesAvailabilitySort,
  ExperienceTag,
  ExperienceTagCount,
  PriceRefinement,
  StartTimeRefinement,
  TripadvisorRatingRefinement,
  ActivitiesAvailabilityProperties,
  ITrackingProperties,
  Attraction,
} from "redmond";
import * as H from "history";
import * as actionTypes from "./constants";
import { trackEvent } from "../../../api/v0/analytics/trackEvent";

export interface IFetchExperiencesAvailability {
  type:
    | actionTypes.FETCH_INITIAL_EXPERIENCES_AVAILABILITY
    | actionTypes.FETCH_MORE_EXPERIENCES_AVAILABILITY
    | actionTypes.FETCH_FILTERED_EXPERIENCES_AVAILABILITY
    | actionTypes.FETCH_EXPERIENCES_AVAILABILITY_REQUEST_PARAMS;
  requestType: ExperiencesAvailabilityRequestEnum;
  history: H.History;
  resetAvailabilityResponse?: boolean;
  resetPriceFilterConstraints?: boolean;
  isDatelessEnabled?: boolean;
}

export const fetchInitialExperiencesAvailability = (
  history: H.History,
  isDatelessEnabled?: boolean
): IFetchExperiencesAvailability => ({
  type: actionTypes.FETCH_INITIAL_EXPERIENCES_AVAILABILITY,
  requestType: ExperiencesAvailabilityRequestEnum.InitialSearch,
  history,
  resetAvailabilityResponse: true,
  isDatelessEnabled,
});

export const fetchFilteredExperiencesAvailability = (
  history: H.History,
  resetPriceFilterConstraints: boolean,
  isDatelessEnabled?: boolean
): IFetchExperiencesAvailability => ({
  type: actionTypes.FETCH_FILTERED_EXPERIENCES_AVAILABILITY,
  requestType: ExperiencesAvailabilityRequestEnum.InitialSearch,
  history,
  resetAvailabilityResponse: true,
  resetPriceFilterConstraints,
  isDatelessEnabled,
});

export const fetchMoreExperiencesAvailability = (
  history: H.History,
  isDatelessEnabled?: boolean
): IFetchExperiencesAvailability => ({
  type: actionTypes.FETCH_MORE_EXPERIENCES_AVAILABILITY,
  requestType: ExperiencesAvailabilityRequestEnum.FollowupSearch,
  history,
  resetAvailabilityResponse: false,
  resetPriceFilterConstraints: false,
  isDatelessEnabled,
});

export const getAvailabilityRequestParameters = (
  history: H.History,
  isDatelessEnabled?: boolean
): IFetchExperiencesAvailability => ({
  type: actionTypes.FETCH_EXPERIENCES_AVAILABILITY_REQUEST_PARAMS,
  requestType: ExperiencesAvailabilityRequestEnum.RequestParams,
  history,
  isDatelessEnabled,
});

export interface IStopFetchMoreExperiencesAvailability {
  type: actionTypes.STOP_FETCH_MORE_EXPERIENCES_AVAILABILITY;
}

export const stopFetchMoreExperiencesAvailability =
  (): IStopFetchMoreExperiencesAvailability => ({
    type: actionTypes.STOP_FETCH_MORE_EXPERIENCES_AVAILABILITY,
  });

export interface ISetExperiencesAvailabilityResults {
  type: actionTypes.SET_EXPERIENCES_AVAILABILITY_RESULTS;
  payload: ExperiencesAvailabilityResponse;
  request: ExperiencesAvailabilityRequest;
  responseType: ExperiencesAvailabilityRequestEnum;
  resetAvailabilityResponse?: boolean;
  resetPriceFilterConstraints?: boolean;
}

export const setExperiencesAvailabilityResults = (args: {
  payload: ExperiencesAvailabilityResponse;
  request: ExperiencesAvailabilityRequest;
  responseType: ExperiencesAvailabilityRequestEnum;
  resetAvailabilityResponse?: boolean;
  resetPriceFilterConstraints?: boolean;
}): ISetExperiencesAvailabilityResults => ({
  type: actionTypes.SET_EXPERIENCES_AVAILABILITY_RESULTS,
  ...args,
});

export interface ISetExperiencesAvailabilityCallStateFailed {
  type: actionTypes.SET_EXPERIENCES_AVAILABILITY_CALL_STATE_FAILED;
}

export const setExperiencesAvailabilityCallStateFailed =
  (): ISetExperiencesAvailabilityCallStateFailed => ({
    type: actionTypes.SET_EXPERIENCES_AVAILABILITY_CALL_STATE_FAILED,
  });

export interface ISetKeyword {
  type: actionTypes.SET_KEYWORD;
  keyword: ExperiencesAvailabilityAutocompleteResult | null;
}

export const setKeyword = (
  keyword: ExperiencesAvailabilityAutocompleteResult | null
): ISetKeyword => ({
  type: actionTypes.SET_KEYWORD,
  keyword,
});

export interface ISetFilterTripadvisorRating {
  type: actionTypes.SET_FILTER_TRIPADVISOR_RATING;
  rating: TripadvisorRatingRefinement;
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>;
}

export const setFilterTripadvisorRating = (
  rating: TripadvisorRatingRefinement,
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>
): ISetFilterTripadvisorRating => {
  if (rating) {
    trackEvent({
      eventName: ACTIVITIES_APPLIED_FILTER,
      properties: {
        ...properties.properties,
        filter_type: "trip_advisor_rating",
      },
    });
  }
  return {
    type: actionTypes.SET_FILTER_TRIPADVISOR_RATING,
    rating,
    properties,
  };
};

export interface ISetFilterStartTimes {
  type: actionTypes.SET_FILTER_START_TIMES;
  startTimes: StartTimeRefinement[];
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>;
}

export const setFilterStartTimes = (
  startTimes: StartTimeRefinement[],
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>
): ISetFilterStartTimes => {
  if (startTimes.length > 0) {
    trackEvent({
      eventName: ACTIVITIES_APPLIED_FILTER,
      properties: {
        ...properties.properties,
        filter_type: "start_times",
      },
    });
  }
  return {
    type: actionTypes.SET_FILTER_START_TIMES,
    startTimes,
    properties,
  };
};

export interface ISetFilterDurationTimes {
  type: actionTypes.SET_FILTER_DURATION;
  durationTimes: DurationRefinement[];
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>;
}

export const setFilterDurationTimes = (
  durationTimes: DurationRefinement[],
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>
): ISetFilterDurationTimes => {
  if (durationTimes.length > 0) {
    trackEvent({
      eventName: ACTIVITIES_APPLIED_FILTER,
      properties: {
        ...properties.properties,
        filter_type: "duration_times",
      },
    });
  }
  return {
    type: actionTypes.SET_FILTER_DURATION,
    durationTimes,
    properties,
  };
};

export interface ISetFilterPrice {
  type: actionTypes.SET_FILTER_PRICE;
  price?: PriceRefinement;
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>;
}

export const setFilterPrice = (
  price: PriceRefinement,
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>
): ISetFilterPrice => {
  if (price) {
    trackEvent({
      eventName: ACTIVITIES_APPLIED_FILTER,
      properties: {
        ...properties.properties,
        filter_type: "price",
      },
    });
  }
  return {
    type: actionTypes.SET_FILTER_PRICE,
    price,
    properties,
  };
};

export interface ISetFilterTags {
  type: actionTypes.SET_FILTER_TAGS;
  tags: ExperienceTag[];
}

export const setFilterTags = (tags: ExperienceTag[]): ISetFilterTags => ({
  type: actionTypes.SET_FILTER_TAGS,
  tags,
});

export interface ISetFilterPopularFeatures {
  type: actionTypes.SET_FILTER_POPULAR_FEATURES;
  tags: ExperienceTag[];
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>;
}

export const setFilterPopularFeatures = (
  tags: ExperienceTag[],
  properties: ITrackingProperties<ActivitiesAvailabilityProperties>
): ISetFilterPopularFeatures => {
  if (tags.length > 0) {
    trackEvent({
      eventName: ACTIVITIES_APPLIED_FILTER,
      properties: {
        ...properties.properties,
        filter_type: "popular_features",
      },
    });
  }
  return {
    type: actionTypes.SET_FILTER_POPULAR_FEATURES,
    tags,
    properties,
  };
};

export interface ISetSortOption {
  type: actionTypes.SET_SORT_OPTION;
  sortOption: ExperiencesAvailabilitySort;
}

export const setSortOption = (
  sortOption: ExperiencesAvailabilitySort
): ISetSortOption => ({
  type: actionTypes.SET_SORT_OPTION,
  sortOption,
});

export interface IResetFilterAndSort {
  type: actionTypes.RESET_FILTER_AND_SORT;
}

export const resetFilterAndSort = (): IResetFilterAndSort => ({
  type: actionTypes.RESET_FILTER_AND_SORT,
});

export interface IFetchExperiencesAvailabilityAutocomplete {
  type: actionTypes.FETCH_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE;
  queryString: string;
}

export const fetchExperiencesAvailabilityAutocomplete = (
  queryString: string
): IFetchExperiencesAvailabilityAutocomplete => ({
  type: actionTypes.FETCH_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE,
  queryString,
});

export interface ISetExperiencesAvailabilityAutocomplete {
  type: actionTypes.SET_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE_RESULTS;
  autocompleteResults: ExperiencesAvailabilityAutocompleteResponse;
}

export const setExperiencesAvailabilityAutocomplete = (
  autocompleteResults: ExperiencesAvailabilityAutocompleteResponse
): ISetExperiencesAvailabilityAutocomplete => ({
  type: actionTypes.SET_EXPERIENCES_AVAILABILITY_AUTOCOMPLETE_RESULTS,
  autocompleteResults,
});

export interface IResetExperiencesAvailability {
  type: actionTypes.RESET_EXPERIENCES_AVAILABILITY;
}

export const resetExperiencesAvailability =
  (): IResetExperiencesAvailability => ({
    type: actionTypes.RESET_EXPERIENCES_AVAILABILITY,
  });

export interface ISetExperienceTagCount {
  type: actionTypes.SET_EXPERIENCE_TAG_COUNT;
  tag?: ExperienceTagCount;
}

export const setSelectedGroupedExperience = (
  tag?: ExperienceTagCount
): ISetExperienceTagCount => ({
  type: actionTypes.SET_EXPERIENCE_TAG_COUNT,
  tag,
});

export interface IFetchBookedActivities {
  type: actionTypes.FETCH_BOOKED_ACTIVITIES;
}

export const fetchBookedActivities = (): IFetchBookedActivities => ({
  type: actionTypes.FETCH_BOOKED_ACTIVITIES,
});

export interface ISetUserHasActivitiesBooked {
  type: actionTypes.SET_USER_HAS_BOOKED_ACTIVITIES;
  userHasActivitiesBooked: boolean;
}

export const setUserHasActivitiesBooked = (
  userHasActivitiesBooked: boolean
): ISetUserHasActivitiesBooked => ({
  type: actionTypes.SET_USER_HAS_BOOKED_ACTIVITIES,
  userHasActivitiesBooked,
});

export type ViewPreference = "grid" | "list";

export interface ISetViewPreference {
  type: actionTypes.SET_VIEW_PREFERENCE;
  viewPreference: ViewPreference;
}

export const setViewPreference = (
  viewPreference: ViewPreference
): ISetViewPreference => ({
  type: actionTypes.SET_VIEW_PREFERENCE,
  viewPreference,
});

export interface IFetchAttraction {
  type: actionTypes.FETCH_ATTRACTION;
  attractionId: number;
}

export const fetchAttraction = (attractionId: number): IFetchAttraction => ({
  type: actionTypes.FETCH_ATTRACTION,
  attractionId,
});

export interface ISetSelectedAttraction {
  type: actionTypes.SET_SELECTED_ATTRACTION;
  attraction?: Attraction;
}

export const setSelectedAttraction = (
  attraction?: Attraction
): ISetSelectedAttraction => ({
  type: actionTypes.SET_SELECTED_ATTRACTION,
  attraction,
});

export type ExperiencesAvailabilityActions =
  | IFetchExperiencesAvailability
  | IStopFetchMoreExperiencesAvailability
  | ISetExperiencesAvailabilityResults
  | ISetExperiencesAvailabilityCallStateFailed
  | IResetExperiencesAvailability
  | ISetKeyword
  | ISetFilterTripadvisorRating
  | ISetFilterStartTimes
  | ISetFilterDurationTimes
  | ISetFilterPrice
  | ISetFilterTags
  | ISetFilterPopularFeatures
  | ISetSortOption
  | IResetFilterAndSort
  | IFetchExperiencesAvailabilityAutocomplete
  | ISetExperiencesAvailabilityAutocomplete
  | ISetExperienceTagCount
  | IFetchBookedActivities
  | ISetUserHasActivitiesBooked
  | ISetViewPreference
  | IFetchAttraction
  | ISetSelectedAttraction;
